'use client';

import React, { useEffect, useState, useContext, useRef } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { supabase } from '../../supabaseClient';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, Brush
} from 'recharts';
import { subWeeks, subMonths, startOfMonth, endOfMonth, format } from 'date-fns';
import Layout from '../Layout';
import { ResponsiveContainer } from 'recharts';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function Analysis() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [measurements, setMeasurements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [showStartPicker, setShowStartPicker] = useState(false);
  const [showEndPicker, setShowEndPicker] = useState(false);
  const [error, setError] = useState(null);
  const [earliestDate, setEarliestDate] = useState(null);
  const [latestDate, setLatestDate] = useState(null);
  const startPickerRef = useRef(null);
  const endPickerRef = useRef(null);

  // State for custom reference ranges
  const [densityRange, setDensityRange] = useState({ min: 142, max: 148 });
  const [compactionRange, setCompactionRange] = useState({ min: 92, max: 100 });
  const [voidsRange, setVoidsRange] = useState({ min: 3, max: 7 });
  const [pendingDensityRange, setPendingDensityRange] = useState({ min: '142', max: '148' });
  const [pendingCompactionRange, setPendingCompactionRange] = useState({ min: '92', max: '100' });
  const [pendingVoidsRange, setPendingVoidsRange] = useState({ min: '3', max: '7' });
  const [rangeError, setRangeError] = useState(null);
  const [densityUnit, setDensityUnit] = useState('pcf'); // 'pcf' or 'kg/m³'

  // Fetch all projects for the dropdown
  useEffect(() => {
    const fetchProjects = async () => {
      if (!user) return;
      setLoading(true);
      try {
        const { data: orgMember, error: orgError } = await supabase
          .from('organization_members')
          .select('organization_id')
          .eq('user_id', user.id)
          .single();

        if (orgError) throw orgError;

        const orgId = orgMember.organization_id;
        const { data: projectsData, error: projectsError } = await supabase
          .from('projects')
          .select('id, name')
          .eq('organization_id', orgId);

        if (projectsError) throw projectsError;
        setProjects(projectsData || []);
      } catch (err) {
        console.error('Error fetching projects:', err);
        setError('Failed to load projects.');
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, [user]);

  // Fetch measurements for the selected project and calculate date boundaries
  useEffect(() => {
    const fetchMeasurements = async () => {
      if (!selectedProjectId || !user) return;
      setLoading(true);
      setError(null);
      try {
        const { data: mappingData, error: mappingError } = await supabase
          .from('project_mapping')
          .select('measurement:measurements(*)')
          .eq('project_id', selectedProjectId);

        if (mappingError) throw mappingError;

        const ms = mappingData.map((item) => item.measurement);
        setMeasurements(ms);

        if (ms.length > 0) {
          const dates = ms.map((m) => new Date(m.LocalTimeOfMeasurement)).filter((d) => !isNaN(d));
          const earliest = new Date(Math.min(...dates));
          const latest = new Date(Math.max(...dates));
          setEarliestDate(earliest);
          setLatestDate(latest);

          setStartDate(earliest);
          setEndDate(latest);
        } else {
          setEarliestDate(null);
          setLatestDate(null);
          setStartDate(null);
          setEndDate(null);
        }
      } catch (err) {
        console.error('Error fetching measurements:', err);
        setError('Failed to load measurements for the selected project.');
      } finally {
        setLoading(false);
      }
    };

    fetchMeasurements();
  }, [selectedProjectId, user]);

  // Click-away handler for date pickers
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        startPickerRef.current &&
        !startPickerRef.current.contains(event.target) &&
        endPickerRef.current &&
        !endPickerRef.current.contains(event.target)
      ) {
        setShowStartPicker(false);
        setShowEndPicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Filter measurements based on date range
  const filteredMeasurements = measurements
    .filter((m) => {
      const measurementDate = new Date(m.LocalTimeOfMeasurement);
      if (startDate && endDate) {
        return measurementDate >= startDate && measurementDate <= endDate;
      }
      return true;
    })
    .sort((a, b) => new Date(a.LocalTimeOfMeasurement) - new Date(b.LocalTimeOfMeasurement));

  // Chart data preparation with full timestamp
  const chartData = filteredMeasurements.map((m) => ({
    timestamp: new Date(m.LocalTimeOfMeasurement).getTime(),
    density: m.Density || 0,
    compaction: m.Compaction || 0,
    voids: m.Voids || 0,
    surfaceTemp: m.SurfaceTemp || 0,
  }));

  useEffect(() => {
    if (chartData.length === 0) return;
  
    const averageDensity = chartData.reduce((sum, item) => sum + item.density, 0) / chartData.length;
  
    if (averageDensity > 1000 && densityUnit !== 'kg/m³') {
      setDensityUnit('kg/m³');
      setDensityRange({ min: 2275, max: 2370 }); // typical asphalt density in kg/m³
      setPendingDensityRange({ min: '2275', max: '2370' });
    } else if (averageDensity <= 1000 && densityUnit !== 'pcf') {
      setDensityUnit('pcf');
      setDensityRange({ min: 142, max: 148 });
      setPendingDensityRange({ min: '142', max: '148' });
    }
  }, [chartData]);

  // Dynamically calculate y-axis min/max for charts
  const getDensityRange = () => {
    const densities = chartData.map((point) => point.density);
    if (densities.length === 0) return { min: 0, max: 100 };

    const min = Math.min(...densities);
    const max = Math.max(...densities);
    const padding = (max - min) * 0.5;
    return {
      min: Math.min(min - padding, densityRange.min - 10),
      max: Math.max(max + padding, densityRange.max + 10),
    };
  };

  const getCompactionRange = () => {
    const compactions = chartData.map((point) => point.compaction);
    if (compactions.length === 0) return { min: 0, max: 100 };

    const min = Math.min(...compactions);
    const max = Math.max(...compactions);
    const padding = (max - min) * 0.5;
    return {
      min: Math.min(min - padding, compactionRange.min - 5),
      max: Math.max(max + padding, compactionRange.max + 5),
    };
  };

  const getVoidsRange = () => {
    const voids = chartData.map((point) => point.voids);
    if (voids.length === 0) return { min: 0, max: 10 };

    const min = Math.min(...voids);
    const max = Math.max(...voids);
    const padding = (max - min) * 0.5;
    return {
      min: Math.min(min - padding, voidsRange.min - 2),
      max: Math.max(max + padding, voidsRange.max + 2),
    };
  };

  // Dynamically calculate preset date ranges based on the dataset
  const setLastWeek = () => {
    if (!latestDate || !earliestDate) return;
    const lastWeekStart = subWeeks(latestDate, 1);
    const newStart = lastWeekStart > earliestDate ? lastWeekStart : earliestDate;
    setStartDate(newStart);
    setEndDate(latestDate);
  };

  const setLastMonth = () => {
    if (!latestDate || !earliestDate) return;
    const lastMonthStart = subMonths(latestDate, 1);
    const newStart = lastMonthStart > earliestDate ? lastMonthStart : earliestDate;
    setStartDate(newStart);
    setEndDate(latestDate);
  };

  const setThisMonth = () => {
    if (!latestDate || !earliestDate) return;
    const monthStart = startOfMonth(latestDate);
    const monthEnd = endOfMonth(latestDate);
    const newStart = monthStart > earliestDate ? monthStart : earliestDate;
    const newEnd = monthEnd < latestDate ? monthEnd : latestDate;
    setStartDate(newStart);
    setEndDate(newEnd);
  };

  const setLastThreeMonths = () => {
    if (!latestDate || !earliestDate) return;
    const threeMonthsAgo = subMonths(latestDate, 3);
    const newStart = threeMonthsAgo > earliestDate ? threeMonthsAgo : earliestDate;
    setStartDate(newStart);
    setEndDate(latestDate);
  };

  const setAllTime = () => {
    if (!latestDate || !earliestDate) return;
    setStartDate(earliestDate);
    setEndDate(latestDate);
  };

  // Handle reference range input changes
  const handleInputChange = (setter, field, value) => {
    setter((prev) => ({ ...prev, [field]: value }));
  };

  // Commit reference range changes with validation
  const commitRangeChange = (setter, pendingRange, setPendingRange, rangeType) => {
    const min = parseFloat(pendingRange.min);
    const max = parseFloat(pendingRange.max);
  
    if (isNaN(min) || isNaN(max)) {
      setRangeError(`Please enter valid numbers for ${rangeType} range.`);
      return;
    }
  
    if (min > max) {
      setRangeError(`Minimum ${rangeType} cannot be greater than maximum.`);
      return;
    }
  
    if (min < 0 || max < 0) {
      setRangeError(`${rangeType} values cannot be negative.`);
      return;
    }
  
    setter({ min, max });
    setRangeError(null);
  };

  // Skeleton Loader Component
  const SkeletonChart = () => (
    <div className="bg-white p-3 rounded-md border-2 border-gray-200 animate-pulse">
      <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
      <div className="h-96 bg-gray-200 rounded"></div>
      <div className="mt-4 h-6 bg-gray-200 rounded w-3/4"></div>
    </div>
  );

  // Calculate trend line for any data
  const calculateTrendLine = (data, xKey, yKey) => {
    const n = data.length;
    if (n < 2) return data.map(item => ({ ...item, trendLine: item[yKey] }));
    
    let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;
    
    data.forEach(point => {
      sumX += point[xKey];
      sumY += point[yKey];
      sumXY += point[xKey] * point[yKey];
      sumX2 += point[xKey] * point[xKey];
    });
    
    const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;
    
    return data.map(point => ({
      ...point,
      trendLine: slope * point[xKey] + intercept
    }));
  };

  // Prepare data with trend lines
  const timeSortedData = [...chartData].sort((a, b) => a.timestamp - b.timestamp);
  const densityDataWithTrendLine = calculateTrendLine(timeSortedData, 'timestamp', 'density');
  const compactionDataWithTrendLine = calculateTrendLine(timeSortedData, 'timestamp', 'compaction');
  const temperatureSortedData = [...chartData].sort((a, b) => a.surfaceTemp - b.surfaceTemp);
  const densityVsTempDataWithTrendLine = calculateTrendLine(temperatureSortedData, 'surfaceTemp', 'density');

  if (loading) {
    return (
      <Layout>
        <div className="p-4">
          <div className="mb-8">
            <div className="mb-6">
              <label htmlFor="project-select" className="block text-sm font-medium text-gray-700 mb-2">
                Select Project
              </label>
              <div className="block w-full sm:w-1/3 h-10 bg-gray-200 rounded-md animate-pulse"></div>
            </div>
          </div>
          <div className="space-y-8">
            <SkeletonChart />
            <SkeletonChart />
            <SkeletonChart />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <style>
        {`
          /* Custom styles for react-datepicker */
          .react-datepicker-wrapper {
            width: 100%;
          }
          .react-datepicker__input-container input {
            width: 100%;
            padding: 8px;
            border: 1px solid #d1d5db;
            border-radius: 0.375rem;
            font-size: 0.875rem;
            color: #374151;
            background-color: #fff;
          }
          .react-datepicker__input-container input:focus {
            outline: none;
            border-color: #facc15;
            box-shadow: 0 0 0 2px rgba(250, 204, 21, 0.2);
          }
          .react-datepicker {
            font-family: Arial, sans-serif;
            border: 1px solid #d1d5db;
            border-radius: 0.375rem;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          }
          .react-datepicker__header {
            background-color: #f7f7f7;
            border-bottom: 1px solid #d1d5db;
            padding: 10px;
          }
          .react-datepicker__current-month {
            font-size: 1rem;
            font-weight: 600;
            color: #374151;
          }
          .react-datepicker__day-name, .react-datepicker__day {
            color: #374151;
            width: 2rem;
            line-height: 2rem;
          }
          .react-datepicker__day--selected {
            background-color: #2196f3 !important;
            color: white !important;
          }
          .react-datepicker__day--today {
            font-weight: bold;
            color: #ff6200;
          }
          .react-datepicker__navigation-icon::before {
            border-color: #374151;
          }
        `}
      </style>
      <div className="p-4">
        {/* Section 1: Project and Date Range Selection */}
        <div className="mb-8">
          <div className="mb-6">
            <label htmlFor="project-select" className="block text-lg font-semibold text-gray-800 mb-3">
              Select a Project to Analyze
            </label>
            <select
              id="project-select"
              value={selectedProjectId}
              onChange={(e) => setSelectedProjectId(e.target.value)}
              className="block w-full sm:w-1/2 py-3 px-4 rounded-lg border-2 border-gray-300 shadow-md focus:border-yellow-500 focus:ring-yellow-500 text-base text-gray-700 placeholder-gray-400 bg-white hover:border-yellow-400 transition-all duration-200"
            >
              <option value="" disabled className="text-gray-400">
                -- Choose a Project --
              </option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
            {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
            {rangeError && <p className="mt-2 text-sm text-red-600">{rangeError}</p>}
          </div>

          {selectedProjectId && (
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">Date Range:</label>
                <div className="relative w-36" ref={startPickerRef}>
                  <DatePicker
                    selected={startDate}
                    onChange={(date) => setStartDate(date)}
                    selectsStart
                    startDate={startDate}
                    endDate={endDate}
                    minDate={earliestDate}
                    maxDate={endDate || latestDate}
                    placeholderText="Start Date"
                    className="block w-full rounded-md border-gray-300 shadow-sm text-sm text-gray-700 bg-white"
                    onFocus={() => {
                      setShowStartPicker(true);
                      setShowEndPicker(false);
                    }}
                    onBlur={() => setShowStartPicker(false)}
                  />
                </div>
                <span className="text-gray-500">to</span>
                <div className="relative w-36" ref={endPickerRef}>
                  <DatePicker
                    selected={endDate}
                    onChange={(date) => setEndDate(date)}
                    selectsEnd
                    startDate={startDate}
                    endDate={endDate}
                    minDate={startDate || earliestDate}
                    maxDate={latestDate}
                    placeholderText="End Date"
                    className="block w-full rounded-md border-gray-300 shadow-sm text-sm text-gray-700 bg-white"
                    onFocus={() => {
                      setShowEndPicker(true);
                      setShowStartPicker(false);
                    }}
                    onBlur={() => setShowEndPicker(false)}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Section 2: Charts or Skeleton Loaders */}
        {selectedProjectId ? (
          filteredMeasurements.length > 0 ? (
            <div className="space-y-8">
              {/* Density Chart */}
              <div className="bg-white p-6 rounded-lg border-2 border-gray-200">
                <div className="flex items-center gap-4 mb-4">
                  <label className="text-sm font-medium text-gray-700">Density Range ({densityUnit}):</label>
                  <input
                    type="text"
                    value={pendingDensityRange.min}
                    onChange={(e) => handleInputChange(setPendingDensityRange, 'min', e.target.value)}
                    className="w-24 rounded-md border-gray-300 shadow-sm text-sm text-gray-700 focus:border-yellow-500 focus:ring-yellow-500"
                    placeholder="Min"
                  />
                  <span className="text-gray-500">to</span>
                  <input
                    type="text"
                    value={pendingDensityRange.max}
                    onChange={(e) => handleInputChange(setPendingDensityRange, 'max', e.target.value)}
                    className="w-24 rounded-md border-gray-300 shadow-sm text-sm text-gray-700 focus:border-yellow-500 focus:ring-yellow-500"
                    placeholder="Max"
                  />
                  <button
                    onClick={() => commitRangeChange(setDensityRange, pendingDensityRange, setPendingDensityRange, 'Density')}
                    className="inline-flex items-center px-3 py-1 bg-yellow-600 text-white text-sm font-medium rounded-md hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  >
                    Update
                  </button>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Density Over Time</h3>
                <ResponsiveContainer width="100%" height={450}>
                  <LineChart
                    data={densityDataWithTrendLine}
                    margin={{ top: 20, right: 30, left: 20, bottom: 30 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis
                      dataKey="timestamp"
                      type="number"
                      domain={['dataMin', 'dataMax']}
                      tickFormatter={(timestamp) => format(new Date(timestamp), 'MMM d, HH:mm')}
                      label={{ value: 'Date and Time', position: 'insideBottom', offset: -10, fill: '#374151', fontSize: 14, fontWeight: 600 }}
                      tick={{ fill: '#6b7280', fontSize: 12 }}
                      tickMargin={10}
                    />
                    <YAxis
                      domain={[getDensityRange().min, getDensityRange().max]}
                      label={{ value: `Density (${densityUnit})`, angle: -90, position: 'left', offset: 10, fill: '#374151', fontSize: 14, fontWeight: 600 }}
                      tick={{ fill: '#6b7280', fontSize: 12 }}
                      tickMargin={10}
                      tickFormatter={(value) => `${value.toFixed(2)}`}
                    />
                    <Tooltip
                      labelFormatter={(timestamp) => format(new Date(timestamp), 'MMM d, yyyy HH:mm')}
                      formatter={(value, name) => {
                        if (name === 'Density' || name === 'Trend Line') {
                          return [`${value.toFixed(2)} ${densityUnit}`, name];
                        }
                        return value;
                      }}
                      contentStyle={{ backgroundColor: '#fff', border: '1px solid #d1d5db', borderRadius: '4px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', padding: '8px' }}
                      labelStyle={{ color: '#374151', fontWeight: 600 }}
                      itemStyle={{ color: '#0ea5e9' }}
                    />
                    <ReferenceLine y={densityRange.max} label={{ value: `${densityRange.max} ${densityUnit}`, position: 'right', fill: '#ef4444', fontSize: 12 }} stroke="#ef4444" strokeDasharray="3 3" />
                    <ReferenceLine y={densityRange.min} label={{ value: `${densityRange.min} ${densityUnit}`, position: 'right', fill: '#ef4444', fontSize: 12 }} stroke="#ef4444" strokeDasharray="3 3" />
                    <Line
                      type="monotone"
                      dataKey="density"
                      stroke="#0ea5e9"
                      name="Density"
                      strokeWidth={0}
                      dot={(props) => {
                        const { cx, cy, payload } = props;
                        const isWithinRange = payload.density >= densityRange.min && payload.density <= densityRange.max;
                        return (
                          <circle
                            cx={cx}
                            cy={cy}
                            r={3}
                            fill={isWithinRange ? '#22c55e' : '#ef4444'}
                            strokeWidth={1}
                            stroke={isWithinRange ? '#22c55e' : '#ef4444'}
                          />
                        );
                      }}
                      activeDot={(props) => {
                        const { cx, cy, payload } = props;
                        const isWithinRange = payload.density >= densityRange.min && payload.density <= densityRange.max;
                        return (
                          <circle
                            cx={cx}
                            cy={cy}
                            r={5}
                            fill={isWithinRange ? '#22c55e' : '#ef4444'}
                            stroke="#fff"
                            strokeWidth={2}
                          />
                        );
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="trendLine"
                      stroke="#ef4444"
                      name="Trend Line"
                      strokeWidth={2}
                      dot={false}
                      strokeDasharray="5 5"
                    />
                    <Brush
                      dataKey="timestamp"
                      height={30}
                      stroke="#0ea5e9"
                      fill="#f3f4f6"
                      travellerWidth={10}
                      tickFormatter={(timestamp) => format(new Date(timestamp), 'MMM d, HH:mm')}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Compaction Chart */}
              <div className="bg-white p-6 rounded-lg border-2 border-gray-200">
                <div className="flex items-center gap-4 mb-4">
                  <label className="text-sm font-medium text-gray-700">Compaction Range (%):</label>
                  <input
                    type="text"
                    value={pendingCompactionRange.min}
                    onChange={(e) => handleInputChange(setPendingCompactionRange, 'min', e.target.value)}
                    className="w-24 rounded-md border-gray-300 shadow-sm text-sm text-gray-700 focus:border-yellow-500 focus:ring-yellow-500"
                    placeholder="Min"
                  />
                  <span className="text-gray-500">to</span>
                  <input
                    type="text"
                    value={pendingCompactionRange.max}
                    onChange={(e) => handleInputChange(setPendingCompactionRange, 'max', e.target.value)}
                    className="w-24 rounded-md border-gray-300 shadow-sm text-sm text-gray-700 focus:border-yellow-500 focus:ring-yellow-500"
                    placeholder="Max"
                  />
                  <button
                    onClick={() => commitRangeChange(setCompactionRange, pendingCompactionRange, setPendingCompactionRange, 'Compaction')}
                    className="inline-flex items-center px-3 py-1 bg-yellow-600 text-white text-sm font-medium rounded-md hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  >
                    Update
                  </button>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Compaction Over Time</h3>
                <ResponsiveContainer width="100%" height={450}>
                  <LineChart
                    data={compactionDataWithTrendLine}
                    margin={{ top: 20, right: 30, left: 20, bottom: 30 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis
                      dataKey="timestamp"
                      type="number"
                      domain={['dataMin', 'dataMax']}
                      tickFormatter={(timestamp) => format(new Date(timestamp), 'MMM d, HH:mm')}
                      label={{ value: 'Date and Time', position: 'insideBottom', offset: -10, fill: '#374151', fontSize: 14, fontWeight: 600 }}
                      tick={{ fill: '#6b7280', fontSize: 12 }}
                      tickMargin={10}
                    />
                    <YAxis
                      domain={[getCompactionRange().min, getCompactionRange().max]}
                      label={{ value: 'Compaction (%)', angle: -90, position: 'left', offset: 10, fill: '#374151', fontSize: 14, fontWeight: 600 }}
                      tick={{ fill: '#6b7280', fontSize: 12 }}
                      tickMargin={10}
                      tickFormatter={(value) => `${value.toFixed(2)}`}
                    />
                    <Tooltip
                      labelFormatter={(timestamp) => format(new Date(timestamp), 'MMM d, yyyy HH:mm')}
                      formatter={(value) => `${value.toFixed(2)} %`}
                      contentStyle={{ backgroundColor: '#fff', border: '1px solid #d1d5db', borderRadius: '4px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', padding: '8px' }}
                      labelStyle={{ color: '#374151', fontWeight: 600 }}
                      itemStyle={{ color: '#82ca9d' }}
                    />
                    <ReferenceLine y={compactionRange.max} label={{ value: `${compactionRange.max}%`, position: 'right', fill: '#ef4444', fontSize: 12 }} stroke="#ef4444" strokeDasharray="3 3" />
                    <ReferenceLine y={compactionRange.min} label={{ value: `${compactionRange.min}%`, position: 'right', fill: '#ef4444', fontSize: 12 }} stroke="#ef4444" strokeDasharray="3 3" />
                    <Line
                      type="monotone"
                      dataKey="compaction"
                      stroke="#82ca9d"
                      name="Compaction"
                      strokeWidth={0}
                      dot={(props) => {
                        const { cx, cy, payload } = props;
                        const isWithinRange = payload.compaction >= compactionRange.min && payload.compaction <= compactionRange.max;
                        return (
                          <circle
                            cx={cx}
                            cy={cy}
                            r= {3}
                            fill={isWithinRange ? '#22c55e' : '#ef4444'}
                            strokeWidth={1}
                            stroke={isWithinRange ? '#22c55e' : '#ef4444'}
                          />
                        );
                      }}
                      activeDot={(props) => {
                        const { cx, cy, payload } = props;
                        const isWithinRange = payload.compaction >= compactionRange.min && payload.compaction <= compactionRange.max;
                        return (
                          <circle
                            cx={cx}
                            cy={cy}
                            r={5}
                            fill={isWithinRange ? '#22c55e' : '#ef4444'}
                            stroke="#fff"
                            strokeWidth={2}
                          />
                        );
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="trendLine"
                      stroke="#ef4444"
                      name="Trend Line"
                      strokeWidth={2}
                      dot={false}
                      strokeDasharray="5 5"
                    />
                    <Brush
                      dataKey="timestamp"
                      height={30}
                      stroke="#82ca9d"
                      fill="#f3f4f6"
                      travellerWidth={10}
                      tickFormatter={(timestamp) => format(new Date(timestamp), 'MMM d, HH:mm')}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Density vs. Surface Temperature Chart */}
              <div className="bg-white p-6 rounded-lg border-2 border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Density vs. Surface Temperature
                </h3>
                <ResponsiveContainer width="100%" height={450}>
                  <LineChart
                    data={densityVsTempDataWithTrendLine}
                    margin={{ top: 20, right: 30, left: 20, bottom: 30 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis
                      dataKey="surfaceTemp"
                      type="number"
                      domain={['dataMin', 'dataMax']}
                      label={{
                        value: 'Surface Temperature (°F)',
                        position: 'insideBottom',
                        offset: -10,
                        fill: '#374151',
                        fontSize: 14,
                        fontWeight: 600,
                      }}
                      tick={{ fill: '#6b7280', fontSize: 12 }}
                      tickMargin={10}
                    />
                    <YAxis
                      label={{
                        value: `Density (${densityUnit})`,
                        angle: -90,
                        position: 'left',
                        offset: 10,
                        fill: '#374151',
                        fontSize: 14,
                        fontWeight: 600,
                      }}
                      tick={{ fill: '#6b7280', fontSize: 12 }}
                      tickMargin={10}
                      tickFormatter={(value) => `${value.toFixed(2)}`}
                      domain={[getDensityRange().min, getDensityRange().max]}
                    />
                    <Tooltip
                      formatter={(value, name) => {
                        if (name === 'Density' || name === 'Trend Line') {
                          return [`${value.toFixed(2)} ${densityUnit}`, name];
                        }
                        return value;
                      }}
                      labelFormatter={(value) => `Surface Temp: ${value} °F`}
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        padding: '8px',
                      }}
                      labelStyle={{ color: '#374151', fontWeight: 600 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="density"
                      stroke="#0ea5e9"
                      name="Density"
                      strokeWidth={0}
                      dot={{
                        r: 3,
                        fill: '#0ea5e9',
                        strokeWidth: 1,
                      }}
                      activeDot={{
                        r: 5,
                        fill: '#0ea5e9',
                        stroke: '#fff',
                        strokeWidth: 2,
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="trendLine"
                      stroke="#ef4444"
                      name="Trend Line"
                      strokeWidth={2}
                      dot={false}
                      strokeDasharray="5 5"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          ) : (
            <div className="space-y-8">
              <SkeletonChart />
              <SkeletonChart />
              <SkeletonChart />
            </div>
          )
        ) : (
          <div className="flex flex-col items-center space-y-6 mt-10">
            <div className="p-4 bg-gray-100 rounded-full">
              <svg
                className="w-12 h-12 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
            </div>
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900">
                No Project Selected
              </h3>
              <p className="mt-2 text-sm text-gray-600">
                Please select a project from the dropdown above to view analysis charts.
              </p>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}